import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  Input,
  Upload,
  message,
  Tag,
  Space,
  Typography,
  Switch,
  List,
  Image,
  Collapse,
  Drawer,
  Divider,
  Empty
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { PlusOutlined, SendOutlined, PaperClipOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { orderIssuesAPI, customerEmployeesAPI, API_BASE_URL } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useIsMobile } from '../hooks/useIsMobile';
import { roleLabel } from '../utils/labels';

const { Text, Paragraph } = Typography;

const DISCREPANCY_OPTIONS = [
  { value: 'wrong_item', label: 'Другое блюдо / перепутали' },
  { value: 'missing', label: 'Не довезли позицию' },
  { value: 'quantity', label: 'Неверное количество' },
  { value: 'quality', label: 'Качество / состояние' },
  { value: 'other', label: 'Другое' }
];

function resolveUploadUrl(path: string) {
  if (!path) return '';
  return path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
}

const OrderIssues: React.FC = () => {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [eligibleFetchId, setEligibleFetchId] = useState(0);
  const [threads, setThreads] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [eligible, setEligible] = useState<any[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [chatThreadId, setChatThreadId] = useState<number | null>(null);
  const [chatDetail, setChatDetail] = useState<any | null>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [replyFiles, setReplyFiles] = useState<UploadFile[]>([]);
  const [replyText, setReplyText] = useState('');
  const [createFiles, setCreateFiles] = useState<UploadFile[]>([]);
  const [settings, setSettings] = useState<{
    showOrderIssuesToAllEmployees: boolean;
    contacts: { id: number; fullName?: string | null; username?: string }[];
  } | null>(null);
  const [employees, setEmployees] = useState<{ id: number; fullName?: string | null; username?: string }[]>([]);
  const [createForm] = Form.useForm();

  const isEmployee = user?.role === 'employee';
  const isCustomer = user?.role === 'customer';
  const isCustomerManager = user?.role === 'customer_manager';
  const isCustomerSide = isCustomer || isCustomerManager;
  const isSupplier = user?.role === 'supplier';
  const isAdmin = user?.role === 'admin';
  const isMobile = useIsMobile();

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const data = await orderIssuesAPI.list();
      setThreads(Array.isArray(data) ? data : []);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось загрузить обращения');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEligible = useCallback(async () => {
    if (!isEmployee && !isCustomerSide) return;
    try {
      const data = await orderIssuesAPI.eligibleOrders();
      setEligible(Array.isArray(data) ? data : []);
    } catch {
      setEligible([]);
    } finally {
      setEligibleFetchId((n) => n + 1);
    }
  }, [isEmployee, isCustomerSide]);

  const loadSettings = useCallback(async () => {
    if (!isCustomer) return;
    try {
      const s = await orderIssuesAPI.getSettings();
      setSettings(s);
      try {
        const em = await customerEmployeesAPI.getEmployees();
        setEmployees(Array.isArray(em) ? em : []);
      } catch {
        setEmployees([]);
      }
    } catch {
      /* ignore */
    }
  }, [isCustomer]);

  useEffect(() => {
    loadThreads();
    loadEligible();
    loadSettings();
  }, [loadThreads, loadEligible, loadSettings]);

  useEffect(() => {
    if (!isEmployee) return;
    const raw = searchParams.get('orderId');
    if (!raw) return;
    if (eligibleFetchId === 0) return;
    const id = parseInt(raw, 10);
    if (Number.isNaN(id)) {
      setSearchParams({}, { replace: true });
      return;
    }
    const ok = eligible.some((o) => o.id === id);
    if (!ok) {
      message.warning('Этот заказ недоступен для обращения (не в списке доставленных для вас).');
      setSearchParams({}, { replace: true });
      return;
    }
    createForm.setFieldsValue({ orderId: id });
    setCreateOpen(true);
    setSearchParams({}, { replace: true });
  }, [isEmployee, eligible, eligibleFetchId, searchParams, createForm, setSearchParams]);

  const openChat = async (id: number) => {
    setChatThreadId(id);
    setChatLoading(true);
    try {
      const d = await orderIssuesAPI.get(id);
      setChatDetail(d);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось открыть чат');
      setChatThreadId(null);
    } finally {
      setChatLoading(false);
    }
  };

  useEffect(() => {
    const rawThread = searchParams.get('threadId');
    if (!rawThread) return;
    const id = parseInt(rawThread, 10);
    if (Number.isNaN(id)) {
      setSearchParams({}, { replace: true });
      return;
    }
    void openChat(id);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const refreshChat = async () => {
    if (chatThreadId == null) return;
    try {
      const d = await orderIssuesAPI.get(chatThreadId);
      setChatDetail(d);
    } catch {
      /* ignore */
    }
  };

  const submitCreate = async () => {
    try {
      const v = await createForm.validateFields();
      if (isCustomerSide) {
        const result = await orderIssuesAPI.openThread({
          orderId: v.orderId,
          discrepancyType: v.discrepancyType || 'other',
          message: v.body || ''
        });
        message.success(result.created ? 'Обращение создано' : 'Открыто существующее обращение');
        setCreateOpen(false);
        createForm.resetFields();
        loadThreads();
        loadEligible();
        if (result.thread?.id) {
          await openChat(result.thread.id);
        }
        return;
      }
      const created =
        createFiles.length > 0
          ? await (async () => {
              const fd = new FormData();
              fd.append('orderId', String(v.orderId));
              fd.append('discrepancyType', v.discrepancyType || 'other');
              fd.append('body', v.body || '');
              createFiles.forEach((f) => {
                if (f.originFileObj) fd.append('photos', f.originFileObj as Blob);
              });
              return orderIssuesAPI.create(fd);
            })()
          : await orderIssuesAPI.createJson({
              orderId: v.orderId,
              discrepancyType: v.discrepancyType || 'other',
              body: v.body || ''
            });
      message.success('Обращение создано');
      setCreateOpen(false);
      createForm.resetFields();
      setCreateFiles([]);
      loadThreads();
      loadEligible();
      if (created?.id) {
        await openChat(created.id);
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.error || 'Ошибка');
    }
  };

  const submitReply = async () => {
    if (chatThreadId == null) return;
    if (!replyText.trim() && replyFiles.length === 0) {
      message.warning('Введите текст или прикрепите фото');
      return;
    }
    try {
      if (replyFiles.length > 0) {
        const fd = new FormData();
        fd.append('body', replyText.trim());
        replyFiles.forEach((f) => {
          if (f.originFileObj) fd.append('photos', f.originFileObj as Blob);
        });
        await orderIssuesAPI.reply(chatThreadId, fd);
      } else {
        await orderIssuesAPI.replyJson(chatThreadId, replyText.trim());
      }
      setReplyText('');
      setReplyFiles([]);
      await refreshChat();
      loadThreads();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не отправлено');
    }
  };

  const toggleResolved = async (id: number, to: 'open' | 'resolved') => {
    try {
      await orderIssuesAPI.setStatus(id, to);
      message.success(to === 'resolved' ? 'Закрыто' : 'Снова открыто');
      loadThreads();
      if (chatThreadId === id) await refreshChat();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Ошибка');
    }
  };

  const saveSettings = async (showAll: boolean, contactIds: number[]) => {
    try {
      const s = await orderIssuesAPI.putSettings({
        showOrderIssuesToAllEmployees: showAll,
        contactUserIds: contactIds
      });
      setSettings(s);
      message.success('Настройки сохранены');
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Ошибка');
    }
  };

  const columns = [
    {
      title: '№',
      width: 60,
      render: (_: any, __: any, i: number) => i + 1
    },
    {
      title: 'Заказ',
      render: (_: any, r: any) => <Text strong>#{r.order?.id}</Text>
    },
    {
      title: 'Тип',
      dataIndex: 'discrepancyType',
      render: (t: string) => DISCREPANCY_OPTIONS.find((o) => o.value === t)?.label || t || '—'
    },
    {
      title: 'Тема',
      key: 'subject',
      render: (_: any, r: any) => {
        const parts: string[] = [];
        if (r.subjectEmployee?.fullName || r.subjectEmployee?.username) {
          parts.push(r.subjectEmployee.fullName || r.subjectEmployee.username);
        }
        if (r.subjectMenuItem?.name) {
          parts.push(r.subjectMenuItem.name);
        }
        return parts.length ? parts.join(' · ') : '—';
      }
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      render: (s: string) =>
        s === 'resolved' ? <Tag color="default">Закрыто</Tag> : <Tag color="processing">Открыто</Tag>
    },
    {
      title: 'Автор',
      render: (_: any, r: any) => r.createdBy?.fullName || r.createdBy?.username || '—'
    },
    {
      title: 'Обновлён',
      dataIndex: 'updatedAt',
      render: (d: string) => (d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '—')
    },
    {
      title: '',
      render: (_: any, r: any) => (
        <Button type="link" onClick={() => openChat(r.id)}>
          Открыть чат
        </Button>
      )
    }
  ];

  return (
    <div>
      <Card title="Расхождения с заказом" style={{ marginBottom: 16 }}>
        <Paragraph type="secondary">
          После доставки сотрудник может сообщить о несоответствии. Заказчик и контролёр видят обращения и могут
          открыть обсуждение с поставщиком по заказу, сотруднику или блюду.
        </Paragraph>
        {(isEmployee || isCustomerSide) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
            style={{ marginBottom: 16 }}
            block={isMobile}
          >
            Новое обращение
          </Button>
        )}
        {isCustomer && settings && (
          <Collapse style={{ marginBottom: 16 }}>
            <Collapse.Panel header="Кто видит переписки по расхождениям" key="settings">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text>Показывать все обращения всем сотрудникам организации: </Text>
                  <Switch
                    checked={settings.showOrderIssuesToAllEmployees}
                    onChange={(c) =>
                      saveSettings(c, settings.contacts.map((x) => x.id))
                    }
                  />
                </div>
                <div>
                  <Text>Ответственные (видят все обращения вашей компании к поставщикам):</Text>
                  <Select
                    mode="multiple"
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder="Выберите сотрудников"
                    value={settings.contacts.map((c) => c.id)}
                    options={employees.map((e) => ({
                      value: e.id,
                      label: e.fullName || e.username || `user ${e.id}`
                    }))}
                    onChange={(ids: number[]) =>
                      saveSettings(settings.showOrderIssuesToAllEmployees, ids)
                    }
                  />
                </div>
              </Space>
            </Collapse.Panel>
          </Collapse>
        )}
        <Table
          rowKey="id"
          loading={loading}
          dataSource={threads}
          columns={columns}
          pagination={{ pageSize: isMobile ? 8 : 12 }}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: isMobile ? 'max-content' : undefined }}
          locale={{ emptyText: <Empty description="Нет обращений" /> }}
        />
      </Card>

      <Modal
        title="Обращение о расхождении"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={submitCreate}
        okText="Отправить"
        width={isMobile ? '100%' : 560}
        centered={!isMobile}
        style={isMobile ? { top: 8, paddingBottom: 0 } : undefined}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item name="orderId" label="Доставленный заказ" rules={[{ required: true }]}>
            <Select
              placeholder="Выберите заказ"
              options={eligible.map((o) => ({
                value: o.id,
                label: `№${o.id} · ${o.supplierLabel} · ${dayjs(o.createdAt).format('DD.MM.YYYY')} · ${o.totalAmount} ₽`
              }))}
            />
          </Form.Item>
          <Form.Item name="discrepancyType" label="Тип несоответствия" initialValue="other">
            <Select options={DISCREPANCY_OPTIONS} />
          </Form.Item>
          <Form.Item name="body" label="Описание" rules={[{ required: true, min: 3 }]}>
            <Input.TextArea rows={4} placeholder="Что не так с заказом?" />
          </Form.Item>
          <Form.Item label="Фотографии">
            <Upload
              multiple
              listType="picture"
              fileList={createFiles}
              beforeUpload={() => false}
              onChange={({ fileList }) => setCreateFiles(fileList)}
              accept="image/jpeg,image/png,image/webp"
            >
              <Button icon={<PaperClipOutlined />}>Прикрепить фото</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={
          chatDetail ? (
            <Space>
              <span>Обращение #{chatDetail.id}</span>
              <Tag>Заказ №{chatDetail.order?.id}</Tag>
            </Space>
          ) : (
            'Чат'
          )
        }
        width={isMobile ? '100%' : 480}
        styles={isMobile ? { body: { paddingBottom: 24 } } : undefined}
        open={chatThreadId !== null}
        onClose={() => {
          setChatThreadId(null);
          setChatDetail(null);
        }}
        extra={
          chatDetail?.canModerate ? (
            <Space>
              {chatDetail.status === 'open' ? (
                <Button size="small" onClick={() => toggleResolved(chatDetail.id, 'resolved')}>
                  Закрыть обращение
                </Button>
              ) : (
                <Button size="small" onClick={() => toggleResolved(chatDetail.id, 'open')}>
                  Открыть снова
                </Button>
              )}
            </Space>
          ) : null
        }
      >
        {chatLoading || !chatDetail ? (
          <Text>Загрузка...</Text>
        ) : (
          <>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {DISCREPANCY_OPTIONS.find((o) => o.value === chatDetail.discrepancyType)?.label}
            </Paragraph>
            <List
              dataSource={chatDetail.messages || []}
              renderItem={(m: any) => (
                <List.Item style={{ alignItems: 'flex-start' }}>
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong>{m.user?.fullName || m.user?.username}</Text>
                        <Tag>{roleLabel(m.user?.role)}</Tag>
                        <Text type="secondary">{dayjs(m.createdAt).format('DD.MM HH:mm')}</Text>
                      </Space>
                    }
                    description={
                      <div>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
                        {(m.attachmentUrls || []).length > 0 && (
                          <Space wrap style={{ marginTop: 8 }}>
                            {(m.attachmentUrls as string[]).map((url) => (
                              <Image
                                key={url}
                                width={100}
                                src={resolveUploadUrl(url)}
                                style={{ borderRadius: 4 }}
                              />
                            ))}
                          </Space>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
            <Divider />
            {chatDetail.status === 'resolved' ? (
              <Text type="secondary">Обращение закрыто. Откройте снова, чтобы писать.</Text>
            ) : (
              <>
                <Input.TextArea
                  rows={3}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Сообщение поставщику или заказчику..."
                />
                <Upload
                  multiple
                  listType="picture"
                  fileList={replyFiles}
                  beforeUpload={() => false}
                  onChange={({ fileList }) => setReplyFiles(fileList)}
                  accept="image/jpeg,image/png,image/webp"
                  style={{ marginTop: 8 }}
                >
                  <Button icon={<PaperClipOutlined />} size="small">
                    Фото
                  </Button>
                </Upload>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  style={{ marginTop: 12 }}
                  onClick={submitReply}
                >
                  Отправить
                </Button>
              </>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default OrderIssues;
